import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Users, Plus, Send, Trash2, MessageCircle, Copy, Mail, Loader2, ArrowRight, UserPlus, Megaphone, Crown, ShieldCheck, Save, BellRing, Bell, X } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { AppTopBar } from "../components/AppScreen";
import PushToggle from "../components/PushToggle";

/* Group Travel ($99/yr) — leader creates a group, invites via email + WhatsApp
   + copyable link, broadcasts nudges to all members. Every invitee must sign
   in with SocialPulse SSO to accept. Members' bags/docs/expenses stay
   private-per-member unless they share. */

/* Inline "Turn on push alerts" banner shown at the top of the Group detail
   view for admins right after group creation — that's the moment the value
   is clearest (reminders, urgent Guardian pings, settle-up nudges land here).
   Dismissed per-group in localStorage so it never nags the same admin twice. */
function GroupPushPrompt({ gid }) {
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(`tp_grp_push_dismiss_${gid}`) === "1"; }
    catch { return false; }
  });
  const [granted, setGranted] = useState(() => {
    try { return typeof Notification !== "undefined" && Notification.permission === "granted"; }
    catch { return false; }
  });
  // Poll permission once every 4s so the banner disappears the moment the
  // browser grants (or if another tab granted). Hook must run unconditionally.
  useEffect(() => {
    if (dismissed || granted) return undefined;
    const t = setInterval(() => {
      try {
        if (typeof Notification !== "undefined" && Notification.permission === "granted") {
          setGranted(true);
        }
      } catch { /* silent */ }
    }, 4000);
    return () => clearInterval(t);
  }, [dismissed, granted]);
  if (dismissed || granted) return null;
  const dismiss = () => {
    try { localStorage.setItem(`tp_grp_push_dismiss_${gid}`, "1"); } catch { /* silent */ }
    setDismissed(true);
  };
  return (
    <div className="relative rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/5 to-accent/5 p-4 sm:p-5" data-testid="group-push-prompt">
      <button onClick={dismiss} aria-label="Dismiss"
        data-testid="group-push-prompt-dismiss"
        className="absolute top-2 right-2 w-8 h-8 rounded-full inline-flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-primary/10">
        <X className="w-4 h-4" />
      </button>
      <div className="flex items-start gap-3 mb-3">
        <div className="w-10 h-10 shrink-0 rounded-xl bg-primary/10 text-primary inline-flex items-center justify-center">
          <Bell className="w-5 h-5" strokeWidth={1.75} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-serif font-800 text-lg text-primary leading-tight">Turn on group alerts</div>
          <div className="text-xs text-muted-foreground mt-0.5 leading-snug">
            Get lock-screen pings when a member sends an urgent Guardian status, when someone owes you money, or when a broadcast lands — even when this tab is closed.
          </div>
        </div>
      </div>
      <PushToggle />
    </div>
  );
}

export default function TravelGroups() {
  const { user } = useAuth();
  const [groups, setGroups] = useState(null);
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();

  const load = () => api.get("/groups").then((r) => setGroups(r.data.items)).catch(() => setGroups([]));
  useEffect(() => { if (user) load(); }, [user]);

  if (!user) return (
    <div data-testid="groups-signin">
      <AppTopBar title="Group Travel" />
      <div className="max-w-md mx-auto px-5 py-16 text-center">
        <Users className="w-10 h-10 mx-auto text-primary mb-3" />
        <h1 className="font-serif font-800 text-2xl">Sign in to open Group Travel</h1>
      </div>
    </div>
  );

  return (
    <div data-testid="groups-page">
      <AppTopBar title="Group Travel" />
      <div className="max-w-4xl mx-auto px-5 py-8">
        <div className="rounded-2xl border border-primary bg-gradient-to-br from-primary to-[#0b2136] text-primary-foreground p-6 mb-6" data-testid="groups-hero">
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-amber-300 mb-2">Group Travel · $99 / year</div>
          <h1 className="font-serif font-800 text-3xl leading-tight">Plan &amp; travel with your crew</h1>
          <p className="text-sm text-primary-foreground/85 mt-2 max-w-lg">Unlimited members. Everyone brings their own bags, documents and expenses — private-per-member, shared when you choose. Broadcast reminders, share the trip manifest, keep the group in sync.</p>
        </div>

        <div className="flex items-center justify-between mb-4">
          <h2 className="font-serif font-800 text-xl text-primary">Your groups</h2>
          <button onClick={() => setCreating(true)} data-testid="groups-create-btn"
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-[hsl(var(--accent))] text-white font-700 rounded-full min-h-[40px] hover:opacity-95 tap">
            <Plus className="w-4 h-4" /> New group
          </button>
        </div>

        {groups === null ? <p className="text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin inline" /> Loading…</p> :
          groups.length === 0 ? (
            <div className="border border-dashed border-border rounded-2xl p-8 text-center bg-card">
              <p className="text-sm text-muted-foreground">No groups yet. Start one for your next trip and invite your travellers.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {groups.map((g) => (
                <Link key={g.id} to={`/groups/${g.id}`} data-testid={`groups-row-${g.id}`}
                  className="flex items-center gap-3 border border-border bg-card p-4 rounded-2xl hover:border-accent hover:shadow-warm-md transition-[box-shadow,border-color] tap">
                  <div className="w-11 h-11 rounded-xl bg-primary text-primary-foreground flex items-center justify-center shrink-0">
                    <Users className="w-5 h-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-serif font-700 text-lg text-primary truncate flex items-center gap-2">
                      {g.title}
                      {g.guardian_urgent_count > 0 && (
                        // Not a nested Link (invalid HTML) — capture the click,
                        // stop propagation so the row's Link doesn't fire, and
                        // route to the Guardian dashboard with the highlight.
                        <button
                          type="button"
                          data-testid={`groups-row-alert-${g.id}`}
                          onClick={(e) => {
                            e.preventDefault(); e.stopPropagation();
                            navigate(`/groups/${g.id}/guardian?highlight=urgent`);
                          }}
                          title="Open Guardian dashboard on the flagged member"
                          className="inline-flex items-center gap-1 bg-rose-600 text-white text-[10px] font-800 uppercase tracking-widest px-2 py-0.5 rounded-full animate-pulse hover:bg-rose-700 focus:outline-none focus:ring-2 focus:ring-rose-400">
                          <ShieldCheck className="w-3 h-3" /> {g.guardian_urgent_count} needs help
                        </button>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground">{g.destination || "—"} · {g.member_count} member{g.member_count !== 1 ? "s" : ""}</div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-primary/60" />
                </Link>
              ))}
            </div>
          )}

        {creating && <CreateDialog onClose={() => setCreating(false)} onCreated={() => { setCreating(false); load(); }} />}
      </div>
    </div>
  );
}

function CreateDialog({ onClose, onCreated }) {
  const [form, setForm] = useState({ title: "", destination: "", description: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!form.title.trim()) { toast.error("Group name required"); return; }
    setBusy(true);
    try { await api.post("/groups", form); toast.success("Group created"); onCreated(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not create"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[80] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-card border border-border rounded-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="groups-create-dialog">
        <h3 className="font-serif font-800 text-xl mb-3">New group</h3>
        <div className="space-y-3">
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
            data-testid="groups-create-title" placeholder="Group name — e.g. Japan 2027"
            className="w-full border border-border rounded-lg px-3 py-2.5 outline-none focus:border-primary" />
          <input value={form.destination} onChange={(e) => setForm({ ...form, destination: e.target.value })}
            data-testid="groups-create-destination" placeholder="Destination (optional)"
            className="w-full border border-border rounded-lg px-3 py-2.5 outline-none focus:border-primary" />
          <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
            data-testid="groups-create-desc" placeholder="What's this group for? (optional)" rows={3}
            className="w-full border border-border rounded-lg px-3 py-2.5 outline-none focus:border-primary" />
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-4 py-2 border border-border rounded-lg text-sm">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="groups-create-save"
            className="px-5 py-2 bg-primary text-primary-foreground font-700 rounded-lg disabled:opacity-60">
            {busy ? "Creating…" : "Create group"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ================= Group Detail ================= */

export function GroupDetail() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [invite, setInvite] = useState(false);
  const [broadcast, setBroadcast] = useState(false);
  const [expTab, setExpTab] = useState(false);
  const [manifestBusy, setManifestBusy] = useState(false);
  const gid = window.location.pathname.split("/")[2];

  const load = () => api.get(`/groups/${gid}`).then((r) => setState(r.data))
    .catch((e) => { toast.error(e?.response?.data?.detail || "Group not found"); navigate("/groups"); });
  useEffect(() => { if (user) load(); }, [user, gid]); // eslint-disable-line

  const downloadGroupManifest = async () => {
    setManifestBusy(true);
    try {
      const res = await api.get(`/groups/${gid}/bag-manifest.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `group-manifest-${gid.slice(0, 8)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("Group manifest downloaded");
    } catch (e) { toast.error(e?.response?.data?.detail || "No bags yet"); }
    finally { setManifestBusy(false); }
  };

  if (!state) return <div data-testid="group-detail-loading"><AppTopBar title="Group" /><div className="max-w-4xl mx-auto px-5 py-8"><Loader2 className="w-5 h-5 animate-spin" /></div></div>;

  const { group, my_role, members } = state;
  const isAdmin = my_role === "admin";
  const active = members.filter((m) => m.status === "active");
  const invited = members.filter((m) => m.status === "invited");

  return (
    <div data-testid="group-detail">
      <AppTopBar title={group.title} />
      <div className="max-w-4xl mx-auto px-5 py-8 space-y-5">
        {isAdmin && <GroupPushPrompt gid={group.id} />}
        <div className="rounded-2xl border border-border bg-card p-5">
          <div className="text-xs font-mono uppercase tracking-widest text-accent">Group</div>
          <h1 className="font-serif font-800 text-3xl text-primary">{group.title}</h1>
          {group.destination && <div className="text-sm text-muted-foreground mt-1">{group.destination}</div>}
          {group.description && <p className="text-base mt-2 text-foreground/80">{group.description}</p>}
          {isAdmin && (
            <div className="flex flex-wrap gap-2 mt-4">
              <button onClick={() => setInvite(true)} data-testid="group-invite-btn"
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary text-primary-foreground rounded-full font-700 min-h-[40px] tap">
                <UserPlus className="w-4 h-4" /> Invite
              </button>
              <button onClick={() => setBroadcast(true)} data-testid="group-broadcast-btn"
                className="inline-flex items-center gap-1.5 px-4 py-2 border border-primary text-primary rounded-full font-700 min-h-[40px] hover:bg-primary hover:text-primary-foreground transition-colors tap">
                <Megaphone className="w-4 h-4" /> Broadcast
              </button>
              <button onClick={downloadGroupManifest} disabled={manifestBusy} data-testid="group-manifest-btn"
                className="inline-flex items-center gap-1.5 px-4 py-2 border border-primary text-primary rounded-full font-700 min-h-[40px] hover:bg-primary hover:text-primary-foreground transition-colors tap disabled:opacity-60">
                {manifestBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />} Group manifest PDF
              </button>
              <button onClick={() => setExpTab(true)} data-testid="group-expenses-btn"
                className="inline-flex items-center gap-1.5 px-4 py-2 border border-emerald-500 text-emerald-700 rounded-full font-700 min-h-[40px] hover:bg-emerald-500 hover:text-white transition-colors tap">
                <Copy className="w-4 h-4" /> Expenses
              </button>
              <Link to={`/groups/${gid}/guardian`} data-testid="group-guardian-link"
                className="inline-flex items-center gap-1.5 px-4 py-2 border border-primary text-primary rounded-full font-700 min-h-[40px] hover:bg-primary hover:text-primary-foreground transition-colors tap">
                <ShieldCheck className="w-4 h-4" /> Guardian
              </Link>
            </div>
          )}
          {!isAdmin && (
            <div className="mt-4 flex flex-wrap gap-2">
              <button onClick={() => setExpTab(true)} data-testid="group-expenses-btn"
                className="inline-flex items-center gap-1.5 px-4 py-2 border border-emerald-500 text-emerald-700 rounded-full font-700 min-h-[40px] hover:bg-emerald-500 hover:text-white transition-colors tap">
                <Copy className="w-4 h-4" /> Expenses
              </button>
              <Link to={`/groups/${gid}/guardian`} data-testid="group-guardian-link"
                className="inline-flex items-center gap-1.5 px-4 py-2 border border-primary text-primary rounded-full font-700 min-h-[40px] hover:bg-primary hover:text-primary-foreground transition-colors tap">
                <ShieldCheck className="w-4 h-4" /> Guardian
              </Link>
            </div>
          )}
        </div>

        <div className="rounded-2xl border border-border bg-card p-5" data-testid="group-members">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-serif font-800 text-lg text-primary">Members ({active.length})</h2>
            {invited.length > 0 && <span className="text-xs text-muted-foreground">{invited.length} invited</span>}
          </div>
          <ul className="divide-y divide-border">
            {[...active, ...invited].map((m) => (
              <li key={m.id} className="flex items-center gap-3 py-2.5" data-testid={`group-member-${m.id}`}>
                <div className={`w-9 h-9 rounded-full flex items-center justify-center font-700 text-sm ${m.status === "active" ? "bg-primary text-primary-foreground" : "bg-zinc-200 text-zinc-600"}`}>
                  {(m.name || m.email)[0]?.toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-700 text-sm truncate flex items-center gap-1.5">
                    {m.name || m.email}
                    {m.role === "admin" && <Crown className="w-3 h-3 text-amber-500" />}
                    {m.status === "invited" && <span className="text-[10px] bg-amber-50 text-amber-800 border border-amber-200 px-1.5 py-0.5 rounded-full">invited</span>}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">{m.email}</div>
                </div>
                {isAdmin && m.role !== "admin" && (
                  <button onClick={async () => { if (window.confirm(`Remove ${m.email}?`)) { await api.delete(`/groups/${gid}/members/${m.id}`); load(); } }}
                    className="p-2 text-zinc-400 hover:text-rose-600" data-testid={`group-member-remove-${m.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>

        {invite && <InviteDialog gid={gid} onClose={() => setInvite(false)} onDone={load} />}
        {broadcast && <BroadcastDialog gid={gid} onClose={() => setBroadcast(false)} />}
        {expTab && <ExpensesDialog gid={gid} members={active} onClose={() => setExpTab(false)} />}
      </div>
    </div>
  );
}

function ExpensesDialog({ gid, members, onClose }) {
  const { user } = useAuth();
  const isAdmin = members.find((m) => m.user_id === user?.user_id)?.role === "admin"
    || members.find((m) => m.user_id === user?.user_id)?.email;  // fallback: admin flag lives on group state
  const [tab, setTab] = useState("list");
  const [expenses, setExpenses] = useState([]);
  const [settlement, setSettlement] = useState(null);
  const [presets, setPresets] = useState([]);
  const [remindBusy, setRemindBusy] = useState(false);
  const [form, setForm] = useState({
    amount: "", currency: "USD", description: "",
    paid_by_user_id: members[0]?.user_id || "",
    split_type: "equal",
    split_details: {},              // {user_id: number}
    split_with_user_ids: members.map((m) => m.user_id),
  });
  const [busy, setBusy] = useState(false);
  const [presetName, setPresetName] = useState("");
  const load = async () => {
    const [e, s, p] = await Promise.all([
      api.get(`/groups/${gid}/expenses`),
      api.get(`/groups/${gid}/expenses/settlement`),
      api.get(`/groups/${gid}/split-presets`).catch(() => ({ data: { items: [] } })),
    ]);
    setExpenses(e.data.items); setSettlement(s.data); setPresets(p.data.items || []);
  };
  useEffect(() => { load().catch(() => {}); }, []); // eslint-disable-line

  const setDetail = (uid, v) => setForm((f) => ({ ...f, split_details: { ...f.split_details, [uid]: v } }));
  const toggleParticipant = (uid) => setForm((f) => ({
    ...f,
    split_with_user_ids: f.split_with_user_ids.includes(uid)
      ? f.split_with_user_ids.filter((x) => x !== uid)
      : [...f.split_with_user_ids, uid],
  }));

  // Helpers to preview validation before submit
  const detailSum = Object.values(form.split_details || {}).reduce((a, b) => a + (Number(b) || 0), 0);
  const detailValid =
    form.split_type === "equal" ? true :
    form.split_type === "amount" ? Math.abs(detailSum - Number(form.amount || 0)) < 0.01 :
    form.split_type === "percent" ? Math.abs(detailSum - 100) < 0.5 :
    /* shares */ detailSum > 0;

  const add = async () => {
    if (!form.amount || !form.description || !form.paid_by_user_id) { toast.error("Fill in amount, description, payer"); return; }
    if (form.split_type !== "equal" && !detailValid) {
      toast.error(form.split_type === "amount" ? `Amounts must sum to ${form.amount}` :
                  form.split_type === "percent" ? "Percentages must sum to 100" :
                  "Enter positive share counts");
      return;
    }
    setBusy(true);
    try {
      // Only send details for the users you're splitting with
      const cleaned = {};
      for (const u of form.split_with_user_ids) {
        const v = Number(form.split_details[u] || 0);
        if (v > 0) cleaned[u] = v;
      }
      await api.post(`/groups/${gid}/expenses`, {
        ...form,
        amount: Number(form.amount),
        split_details: form.split_type === "equal" ? {} : cleaned,
      });
      setForm({ ...form, amount: "", description: "", split_details: {} });
      load(); toast.success("Expense added");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add"); }
    finally { setBusy(false); }
  };
  const del = async (id) => { if (window.confirm("Delete?")) { await api.delete(`/groups/${gid}/expenses/${id}`); load(); } };

  const nameOf = (uid) => members.find((m) => m.user_id === uid)?.name || members.find((m) => m.user_id === uid)?.email || uid?.slice(0, 6);

  const suffix = form.split_type === "amount" ? form.currency :
                 form.split_type === "percent" ? "%" :
                 form.split_type === "shares" ? "sh" : "";

  const applyPreset = (p) => {
    setForm((f) => ({
      ...f,
      split_type: p.split_type,
      split_details: { ...(p.split_details || {}) },
      split_with_user_ids: (p.split_with_user_ids && p.split_with_user_ids.length)
        ? p.split_with_user_ids
        : f.split_with_user_ids,
    }));
    toast.success(`Applied "${p.name}"`);
  };

  const savePreset = async () => {
    const name = presetName.trim();
    if (!name) { toast.error("Give this split a name"); return; }
    if (form.split_type === "equal") { toast.error("Equal splits don't need a preset"); return; }
    try {
      const cleaned = {};
      for (const u of form.split_with_user_ids) {
        const v = Number(form.split_details[u] || 0);
        if (v > 0) cleaned[u] = v;
      }
      const { data } = await api.post(`/groups/${gid}/split-presets`, {
        name,
        split_type: form.split_type,
        split_details: cleaned,
        split_with_user_ids: form.split_with_user_ids,
      });
      setPresets((cur) => [data, ...cur]);
      setPresetName("");
      toast.success("Preset saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save preset");
    }
  };

  const deletePreset = async (pid) => {
    if (!window.confirm("Delete this preset?")) return;
    try {
      await api.delete(`/groups/${gid}/split-presets/${pid}`);
      setPresets((cur) => cur.filter((p) => p.id !== pid));
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not delete"); }
  };

  const sendReminders = async () => {
    if (!window.confirm("Send an email to every member who still owes money?")) return;
    setRemindBusy(true);
    try {
      const { data } = await api.post(`/groups/${gid}/expenses/settlement/remind`, {});
      if (data.sent === 0 && data.total_debtors === 0) {
        toast.success("Everyone is already settled up 🎉");
      } else {
        toast.success(`Reminders sent to ${data.sent}/${data.total_debtors} member${data.total_debtors === 1 ? "" : "s"}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send reminders");
    } finally { setRemindBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-card border border-border rounded-2xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="group-expenses-dialog">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-serif font-800 text-xl">Group expenses</h3>
          <div className="flex items-center gap-1 text-xs">
            {["list", "add", "settle"].map((k) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`group-exp-tab-${k}`}
                className={`px-3 py-1.5 rounded-full font-700 ${tab === k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-primary"}`}>
                {k === "list" ? "All" : k === "add" ? "Add" : "Settle up"}
              </button>
            ))}
          </div>
        </div>

        {tab === "add" && (
          <div className="space-y-3" data-testid="group-exp-add">
            {presets.length > 0 && (
              <div className="border border-border rounded-lg p-2 bg-secondary/40" data-testid="group-exp-presets">
                <div className="text-[10px] font-mono uppercase tracking-widest text-accent mb-1.5">Apply preset</div>
                <div className="flex flex-wrap gap-1.5">
                  {presets.map((p) => (
                    <span key={p.id} className="inline-flex items-center gap-1 border border-border bg-card rounded-full pl-3 pr-1 py-0.5 text-xs">
                      <button onClick={() => applyPreset(p)} data-testid={`group-exp-preset-${p.id}`}
                        className="font-700 text-primary hover:underline">
                        {p.name}
                      </button>
                      <span className="text-muted-foreground text-[10px]">· {p.split_type}</span>
                      <button onClick={() => deletePreset(p.id)} data-testid={`group-exp-preset-del-${p.id}`}
                        className="w-5 h-5 rounded-full text-zinc-400 hover:text-rose-600 inline-flex items-center justify-center">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <input value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })}
                type="number" step="0.01" placeholder="Amount" data-testid="group-exp-amount"
                className="w-full border border-border rounded-lg px-3 py-2.5 outline-none focus:border-primary" />
              <input value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })}
                maxLength={3} placeholder="USD" data-testid="group-exp-currency"
                className="w-full border border-border rounded-lg px-3 py-2.5 outline-none focus:border-primary" />
            </div>
            <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="What was this for?" data-testid="group-exp-desc"
              className="w-full border border-border rounded-lg px-3 py-2.5 outline-none focus:border-primary" />
            <select value={form.paid_by_user_id} onChange={(e) => setForm({ ...form, paid_by_user_id: e.target.value })}
              data-testid="group-exp-payer"
              className="w-full border border-border rounded-lg px-3 py-2.5">
              {members.map((m) => <option key={m.user_id} value={m.user_id}>Paid by {m.name || m.email}</option>)}
            </select>

            {/* Split type */}
            <div>
              <div className="text-xs font-mono uppercase tracking-widest text-accent mb-1.5">Split type</div>
              <div className="grid grid-cols-4 gap-1.5" data-testid="group-exp-split-type">
                {[
                  ["equal", "Equal", "Split evenly"],
                  ["amount", "Amount", "Enter $ per person"],
                  ["percent", "Percent", "Enter % per person"],
                  ["shares", "Shares", "e.g. 2 for a couple"],
                ].map(([v, l, hint]) => (
                  <button key={v} onClick={() => setForm({ ...form, split_type: v, split_details: {} })}
                    data-testid={`group-exp-split-${v}`}
                    title={hint}
                    className={`text-xs font-700 px-2 py-2 rounded-lg border ${form.split_type === v ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:border-primary"}`}>
                    {l}
                  </button>
                ))}
              </div>
            </div>

            {/* Per-member rows */}
            <div className="border border-border rounded-lg divide-y divide-border" data-testid="group-exp-split-rows">
              {members.map((m) => {
                const included = form.split_with_user_ids.includes(m.user_id);
                return (
                  <div key={m.user_id} className={`flex items-center gap-2 p-2 ${!included ? "opacity-50" : ""}`}>
                    <input type="checkbox" checked={included} onChange={() => toggleParticipant(m.user_id)}
                      data-testid={`group-exp-participant-${m.user_id}`}
                      className="w-4 h-4 accent-primary" />
                    <div className="min-w-0 flex-1 text-sm truncate">{m.name || m.email}</div>
                    {form.split_type !== "equal" && included && (
                      <div className="flex items-center gap-1 shrink-0">
                        <input type="number" step="0.01" min="0"
                          value={form.split_details[m.user_id] ?? ""}
                          onChange={(e) => setDetail(m.user_id, e.target.value)}
                          data-testid={`group-exp-detail-${m.user_id}`}
                          placeholder="0"
                          className="w-20 border border-border rounded px-2 py-1 text-sm text-right outline-none focus:border-primary" />
                        <span className="text-xs text-muted-foreground w-8">{suffix}</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {form.split_type !== "equal" && (
              <div className={`text-xs ${detailValid ? "text-emerald-700" : "text-rose-700"} font-700`} data-testid="group-exp-split-validity">
                Sum: {detailSum.toFixed(2)}{suffix ? ` ${suffix}` : ""}
                {form.split_type === "amount" && ` / ${Number(form.amount || 0).toFixed(2)} ${form.currency}`}
                {form.split_type === "percent" && " / 100%"}
                {detailValid ? " ✓" : " — doesn't balance"}
              </div>
            )}

            <button onClick={add} disabled={busy || (form.split_type !== "equal" && !detailValid)}
              data-testid="group-exp-add-save"
              className="w-full px-5 py-2.5 bg-emerald-600 text-white font-700 rounded-lg disabled:opacity-60">
              {busy ? "Adding…" : "Add expense"}
            </button>

            {form.split_type !== "equal" && detailValid && (
              <div className="border border-dashed border-border rounded-lg p-2.5 flex items-center gap-2" data-testid="group-exp-save-preset">
                <input value={presetName} onChange={(e) => setPresetName(e.target.value)}
                  placeholder='Save this split as… (e.g. "Rooms 2/2/1")'
                  data-testid="group-exp-preset-name"
                  className="flex-1 min-w-0 border border-border rounded-lg px-3 py-1.5 text-sm outline-none focus:border-primary" />
                <button onClick={savePreset} data-testid="group-exp-preset-save"
                  className="shrink-0 inline-flex items-center gap-1 px-3 py-1.5 border border-primary text-primary rounded-lg text-sm font-700 hover:bg-primary hover:text-primary-foreground">
                  <Save className="w-3.5 h-3.5" /> Save preset
                </button>
              </div>
            )}
          </div>
        )}

        {tab === "list" && (
          <div className="space-y-2" data-testid="group-exp-list">
            {expenses.length === 0 ? <p className="text-sm text-muted-foreground">No expenses yet.</p> : expenses.map((e) => (
              <div key={e.id} className="flex items-center justify-between border border-border rounded-lg p-3 gap-2" data-testid={`group-exp-row-${e.id}`}>
                <div className="min-w-0">
                  <div className="font-700 text-sm truncate">{e.description}</div>
                  <div className="text-xs text-muted-foreground">Paid by {nameOf(e.paid_by_user_id)} · {e.split_type} split · {e.split_with_user_ids.length} people</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-mono font-700 text-primary">{e.currency} {e.amount.toFixed(2)}</div>
                  <button onClick={() => del(e.id)} className="text-xs text-rose-600 hover:underline" data-testid={`group-exp-del-${e.id}`}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "settle" && settlement && (
          <div className="space-y-3" data-testid="group-exp-settle">
            {settlement.currencies.length === 0 && <p className="text-sm text-muted-foreground">Nothing to settle yet.</p>}
            {settlement.currencies.length > 0 && (
              <div className="flex items-center justify-between border border-emerald-200 bg-emerald-50 rounded-lg px-3 py-2">
                <div className="text-xs text-emerald-800">Nudge every debtor with an email — they'll see exactly what they owe and a link back to the group.</div>
                <button onClick={sendReminders} disabled={remindBusy}
                  data-testid="group-exp-remind-btn"
                  className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 text-white font-700 rounded-full text-xs disabled:opacity-60">
                  {remindBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <BellRing className="w-3.5 h-3.5" />}
                  Send reminders
                </button>
              </div>
            )}
            {settlement.currencies.map((c) => (
              <div key={c.currency} className="border border-border rounded-2xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs font-mono uppercase tracking-widest text-accent">{c.currency}</div>
                  <div className="text-sm text-muted-foreground">Total {c.currency} {c.total.toFixed(2)}</div>
                </div>
                <div className="text-xs mb-2">
                  <strong>Net balances:</strong>{" "}
                  {c.balances.map((b) => (
                    <span key={b.user_id} className={`inline-block mx-1 ${b.net > 0 ? "text-emerald-700" : b.net < 0 ? "text-rose-700" : "text-muted-foreground"}`}>
                      {b.name}: {b.net >= 0 ? "+" : ""}{b.net.toFixed(2)}
                    </span>
                  ))}
                </div>
                <div className="space-y-1.5" data-testid={`group-exp-settlements-${c.currency}`}>
                  {c.settlements.length === 0 ? <p className="text-sm text-emerald-700">All settled ✓</p> : c.settlements.map((s, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm bg-emerald-50 border border-emerald-200 rounded-lg p-2.5">
                      <span className="font-700">{s.from_name}</span>
                      <ArrowRight className="w-4 h-4 text-emerald-600" />
                      <span className="font-700">{s.to_name}</span>
                      <span className="ml-auto font-mono font-700 text-emerald-800">{s.currency} {s.amount.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="flex justify-end mt-4">
          <button onClick={onClose} className="px-4 py-2 border border-border rounded-lg text-sm">Close</button>
        </div>
      </div>
    </div>
  );
}

function InviteDialog({ gid, onClose, onDone }) {
  const [form, setForm] = useState({ email: "", name: "", role: "member" });
  const [busy, setBusy] = useState(false);
  const [links, setLinks] = useState(null);
  const send = async () => {
    if (!form.email.trim()) return;
    setBusy(true);
    try { const r = await api.post(`/groups/${gid}/invite`, form); setLinks(r.data); toast.success("Invitation email sent"); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not invite"); }
    finally { setBusy(false); }
  };
  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Link copied"); };
  return (
    <div className="fixed inset-0 z-[80] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-card border border-border rounded-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="group-invite-dialog">
        {!links ? (
          <>
            <h3 className="font-serif font-800 text-xl mb-3">Invite a member</h3>
            <div className="space-y-3">
              <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                type="email" data-testid="group-invite-email" placeholder="member@example.com"
                className="w-full border border-border rounded-lg px-3 py-2.5 outline-none focus:border-primary" />
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="group-invite-name" placeholder="Name (optional)"
                className="w-full border border-border rounded-lg px-3 py-2.5 outline-none focus:border-primary" />
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
                data-testid="group-invite-role"
                className="w-full border border-border rounded-lg px-3 py-2.5 outline-none">
                <option value="member">Member</option>
                <option value="admin">Admin (can invite &amp; broadcast)</option>
              </select>
              <p className="text-xs text-muted-foreground">They'll sign in with SocialPulse SSO — their bags, docs, expenses stay private-per-member.</p>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={onClose} className="px-4 py-2 border border-border rounded-lg text-sm">Cancel</button>
              <button onClick={send} disabled={busy} data-testid="group-invite-send"
                className="px-5 py-2 bg-primary text-primary-foreground font-700 rounded-lg disabled:opacity-60 inline-flex items-center gap-1.5">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send
              </button>
            </div>
          </>
        ) : (
          <>
            <h3 className="font-serif font-800 text-xl mb-2">Invitation sent</h3>
            <p className="text-sm text-muted-foreground mb-3">Also share via:</p>
            <a href={links.wa_link} target="_blank" rel="noreferrer" data-testid="group-invite-wa"
              className="flex items-center justify-between gap-2 border border-emerald-300 bg-emerald-50 text-emerald-800 rounded-lg px-4 py-2.5 mb-2 hover:bg-emerald-100">
              <span className="inline-flex items-center gap-2 font-700"><MessageCircle className="w-4 h-4" /> WhatsApp</span>
              <ArrowRight className="w-4 h-4" />
            </a>
            <button onClick={() => copy(links.join_url)} data-testid="group-invite-copy"
              className="w-full flex items-center justify-between gap-2 border border-border rounded-lg px-4 py-2.5 mb-3 hover:border-primary">
              <span className="inline-flex items-center gap-2 font-700"><Copy className="w-4 h-4" /> Copy link</span>
              <span className="text-xs text-muted-foreground truncate max-w-[180px]">{links.join_url}</span>
            </button>
            <div className="flex justify-end">
              <button onClick={onClose} className="px-4 py-2 border border-border rounded-lg text-sm">Done</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function BroadcastDialog({ gid, onClose }) {
  const [form, setForm] = useState({ subject: "", message: "" });
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!form.subject.trim() || !form.message.trim()) return;
    setBusy(true);
    try { const r = await api.post(`/groups/${gid}/broadcast`, form); toast.success(`Delivered to ${r.data.delivered}/${r.data.total}`); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[80] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-card border border-border rounded-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="group-broadcast-dialog">
        <h3 className="font-serif font-800 text-xl mb-3">Broadcast to the group</h3>
        <input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })}
          data-testid="group-broadcast-subject" placeholder="Subject — e.g. Bag drop reminder"
          className="w-full border border-border rounded-lg px-3 py-2.5 mb-2 outline-none focus:border-primary" />
        <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
          data-testid="group-broadcast-message" placeholder="Message…" rows={5}
          className="w-full border border-border rounded-lg px-3 py-2.5 outline-none focus:border-primary" />
        <p className="text-xs text-muted-foreground mt-2">Every active member gets an email + in-app notification.</p>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-4 py-2 border border-border rounded-lg text-sm">Cancel</button>
          <button onClick={send} disabled={busy} data-testid="group-broadcast-send"
            className="px-5 py-2 bg-[hsl(var(--accent))] text-white font-700 rounded-lg disabled:opacity-60 inline-flex items-center gap-1.5">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Megaphone className="w-4 h-4" />} Send broadcast
          </button>
        </div>
      </div>
    </div>
  );
}

/* ================= Group Join (public + auth) ================= */

export function GroupJoin() {
  const { user, openLogin } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token");
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) { setErr("Missing invite token"); return; }
    api.get("/groups/join/info", { params: { token } }).then((r) => setInfo(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Invalid invitation"));
  }, [token]);

  const accept = async () => {
    setBusy(true);
    try { const r = await api.post("/groups/join", { token }); toast.success("Welcome to the group!"); navigate(`/groups/${r.data.group_id}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not join"); }
    finally { setBusy(false); }
  };

  if (err) return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="max-w-md w-full bg-card border border-border rounded-2xl p-8 text-center shadow-warm-md" data-testid="group-join-error">
        <Users className="w-10 h-10 text-primary/40 mx-auto mb-3" />
        <p className="font-serif text-lg text-primary">{err}</p>
      </div>
    </div>
  );
  if (!info) return <div className="min-h-screen bg-background" />;

  return (
    <div className="min-h-screen bg-background" data-testid="group-join-page">
      <AppTopBar title="Group invitation" />
      <div className="max-w-md mx-auto px-5 py-10">
        <div className="rounded-2xl bg-card border border-border p-6 shadow-warm-md text-center">
          <Users className="w-10 h-10 text-primary mx-auto mb-3" />
          <div className="text-xs font-mono uppercase tracking-widest text-accent">You're invited to join</div>
          <h1 className="font-serif font-800 text-2xl text-primary mt-1">{info.group?.title}</h1>
          {info.group?.destination && <div className="text-sm text-muted-foreground mt-1">{info.group.destination}</div>}
          {info.group?.description && <p className="text-sm text-foreground/80 mt-3">{info.group.description}</p>}
          <p className="text-xs text-muted-foreground mt-4">Invited: <strong>{info.invited_email}</strong></p>
          <div className="mt-6">
            {!user ? (
              <button onClick={openLogin} data-testid="group-join-signin"
                className="w-full px-6 py-3 bg-primary text-primary-foreground rounded-full font-700 min-h-[48px]">
                Sign in with SocialPulse to accept
              </button>
            ) : user.email?.toLowerCase() !== info.invited_email?.toLowerCase() ? (
              <p className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-3">
                You're signed in as <strong>{user.email}</strong>, but this invitation is for <strong>{info.invited_email}</strong>. Please sign out and sign in with the invited email.
              </p>
            ) : (
              <button onClick={accept} disabled={busy} data-testid="group-join-accept"
                className="w-full px-6 py-3 bg-[hsl(var(--accent))] text-white rounded-full font-700 min-h-[48px] disabled:opacity-60 inline-flex items-center justify-center gap-2">
                {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <ArrowRight className="w-5 h-5" />} Accept &amp; join
              </button>
            )}
          </div>
        </div>
        <p className="text-xs text-center text-muted-foreground mt-4">Your bags, documents and expenses stay private to your account.</p>
      </div>
    </div>
  );
}
